import {DOM} from '@levcharity/resources/scripts/helpers/DOM';
import {Helper} from '@levcharity/resources/scripts/helpers/Helper';
import {ChoiceSelect} from '@levcharity/resources/scripts/components/ChoiceSelect';

export class MyAccountStats {
    #chartData = {};
    #totalsList = {};
    #chartDataAlreadyFetched = false;
    #activeTimeline = '1y';

    #fetching = {};

    constructor() {
        if (!DOM.el('.accounts_bar') && !DOM.el('.custom_bar_chart_wrapper')) {
            return;
        }
        this.#bindAccountsBarTabs();
        this.#fetchData();
        this.#addEvents();
    }

    #bindAccountsBarTabs() {
        DOM.addEvent('click', '.accounts_bar .buttons_group button', (event, button) => {
            event.preventDefault();
            const accountsBar = DOM.el('.accounts_bar');
            if (!accountsBar) {
                return;
            }

            const current = DOM.el('.accounts_bar .buttons_group button.active');
            current?.classList.remove('active');
            button.classList.add('active');

            const recent = DOM.el('.recent_donations', accountsBar);
            const tops = DOM.el('.tops', accountsBar);
            const topPrizes = DOM.el('.top_prizes', accountsBar);

            if (!recent || !tops) {
                return;
            }

            if (button.classList.contains('recent_btn')) {
                recent.classList.add('is-active');
                tops.classList.remove('is-active');
                topPrizes?.classList.remove('is-active');
            } else if (button.classList.contains('top_prizes_btn')) {
                recent.classList.remove('is-active');
                tops.classList.remove('is-active');
                topPrizes?.classList.add('is-active');
            } else {
                recent.classList.remove('is-active');
                tops.classList.add('is-active');
                topPrizes?.classList.remove('is-active');
            }
        });
    }

    async #fetchData() {
        this.#fetchStatsChart()
        this.#fetchDonations();
        this.#datatable();
    }

    async #fetchStatsChart() {
        if (this.#fetching?.chart) return;
        const statsChartWrapper = DOM.el('.custom_bar_chart_wrapper');
        if (!statsChartWrapper?.dataset?.action) {
            return;
        }

        const {Helper} = await import('@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "levcharity-helper" */);

        this.#fetching.chart = true;
        Helper.request(admin_ajax[0], {
            action: statsChartWrapper.dataset.action,
            token: statsChartWrapper.dataset.token,
            timeline: this.#activeTimeline
        })
            .then((response) => {
                if (response.success) {
                    this.#chartData = {
                        totalToday: response.total_today,
                        totalRaised: response.total_raised,
                        totalDonors: response.total_donors,
                        teamMembers: response.team_members,
                        goal: response.goal,
                        data: response.chart_data
                    }
                    this.#renderChart();
                    this.#chartDataAlreadyFetched = true;
                }

                this.#fetching.chart = false;
            })
            .catch((error) => {
                console.error(error)
                this.#fetching.chart = false;
            })
    }

    async #fetchDonations() {
        if (this.#fetching?.donations) return;
        const accountsBarWrapper = DOM.el('.accounts_bar');
        if (!accountsBarWrapper?.dataset?.action) {
            return;
        }

        const {Helper} = await import('@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "levcharity-helper" */);

        this.#fetching.totals = true;
        Helper.request(admin_ajax[0], {
            action: accountsBarWrapper.dataset.action,
            token: accountsBarWrapper.dataset.token,
        })
            .then((response) => {
                if (response.success) {
                    this.#totalsList = {
                        recentDonations: response.recent_donations,
                        participants: response?.participants || [],
                        topDonations: response?.top_donations || [],
                        topPrizes: response?.top_prizes || [],
                    }
                }

                this.#renderTotals();
                this.#fetching.totals = false;
            })
            .catch((error) => {
                console.error(error)
                this.#fetching.totals = false;
            })

    }

    async #addEvents() {
        const {gsap} = await import('gsap' /* webpackChunkName: "gsap" */ )

        let timeout = null;

        DOM.addEvent('mouseover', '.custom_bar_chart_wrapper .chart_bar:not(.empty)', (event, bar) => {
            if (!window.matchMedia("(pointer: coarse)").matches) {
                clearTimeout(timeout);
                timeout = null;
                this.#activateChartBar(bar);
            }
        });
        DOM.addEvent('mouseout', '.custom_bar_chart_wrapper .chart_bar:not(.empty)', (event, bar) => {
            if (!window.matchMedia("(pointer: coarse)").matches) {
                timeout = setTimeout(() => {
                    this.#deactivateChartBar(bar);
                }, 300);
            }
        });

        DOM.addEvent('click', '.custom_bar_chart_wrapper .chart_bar:not(.empty)', (event, bar) => {
            const active = DOM.el('.custom_bar_chart_wrapper .chart_bar.active')
            if (active) {
                const vars = this.#deactivateChartBar(active, timeout, interval);
                interval = vars.interval
                timeout = vars.timeout
            }
            const vars = this.#activateChartBar(bar, timeout, interval);
            interval = vars.interval
            timeout = vars.timeout
        })

        DOM.addEvent('click', document, (event, bar) => {
            if (!DOM.compareElement(event.target, '.custom_bar_chart_wrapper .chart_bar:not(.empty)')) {
                const active = DOM.el('.custom_bar_chart_wrapper .chart_bar.active')
                if (active) {
                    const vars = this.#deactivateChartBar(active, timeout, interval);
                    interval = vars.interval
                    timeout = vars.timeout
                }
            }
        })

        DOM.addEvent('click', '.custom_bar_chart_wrapper .period_buttons button', (event, button) => {
            event.preventDefault();
            if (this.#fetching?.chart) return;
            this.#activeTimeline = button.dataset.period;
            const currentActive = DOM.el('.custom_bar_chart_wrapper .period_buttons button.active')
            currentActive.classList.remove('active')
            button.classList.add('active');

            this.#fetchStatsChart();
        })
        DOM.addEvent('click', '.html_chart_wrapper .arrow-left', (event) => {
           const scrollChartBar = DOM.el('.html_chart');
           scrollChartBar.scrollBy({ left: -150, behavior: "smooth" })
        });

        DOM.addEvent('click', '.html_chart_wrapper .arrow-right', (event) => {
            const scrollChartBar = DOM.el('.html_chart');
            scrollChartBar.scrollBy({ left: 150, behavior: "smooth" })
        });

        if (typeof wp !== 'undefined' && wp.hooks) {
        wp.hooks.addAction('levCharityRenderPopupHtml', 'w4f', (button, wrapper) => {
            if (button.classList.contains('my_account_switch_team')) {
                wrapper.classList.add('my_account_switch_team_modal_wrapper');
                (new ChoiceSelect()).singleSelect({})
            }
        })
        }

        DOM.addEvent('change', '.my_account_switch_team_modal_wrapper #other_team', (event, checkbox) => {
            const otherNameWrapper = DOM.el('.my_account_switch_team_modal_wrapper .other_team_name')
            if (checkbox.checked) {
                otherNameWrapper.classList.remove('hidden')
            } else {
                otherNameWrapper.classList.add('hidden')
            }
        })

        DOM.addEvent('click', '.my_account_switch_team_modal_wrapper .primary_button', async (event, button) => {
            event.preventDefault();

            const form = button.closest('form');
            if (!form) return;

            const formdata = new FormData(form);

            // Resolve admin-ajax URL robustly
            const ajaxUrl =
                (window.admin_ajax && (admin_ajax.url || admin_ajax[0])) ||
                '/wp-admin/admin-ajax.php';

            // Fallback toast if levToast isn't ready
            const Toast = window.levToast ?? {
                success: (m) => console.log('[toast:success]', m),
                error:   (m) => console.error('[toast:error]', m),
            };

            // Small bottom message
            const showBottomMessage = (text, color = '#28a745') => {
                const msg = document.createElement('div');
                msg.textContent = text;
                Object.assign(msg.style, {
                    position: 'fixed',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: color,
                    color: '#fff',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    zIndex: '9999',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    opacity: '0',
                    transition: 'opacity 0.3s ease',
                    maxWidth: '90%',
                    textAlign: 'center',
                    wordBreak: 'break-word',
                });
                document.body.appendChild(msg);
                requestAnimationFrame(() => (msg.style.opacity = '1'));
                setTimeout(() => {
                    msg.style.opacity = '0';
                    setTimeout(() => msg.remove(), 500);
                }, 3000);
            };

            // Extract a human message from various server shapes
            const getMessage = (resp, fallback = 'Something went wrong') => {
                if (!resp) return fallback;

                // Common WP shapes:
                // wp_send_json_error( 'Message' ) => { success:false, data: "Message" }
                // wp_send_json_error( ['Message'] ) => { success:false, data: ["Message"] }
                // Custom: { success:false, message:"Message" }
                // Or nested: { success:false, data:{ message:"Message" } }
                const candidates = [
                    resp.message,
                    resp?.data?.message,
                    Array.isArray(resp?.data) ? resp.data.join('\n') : undefined,
                    typeof resp?.data === 'string' ? resp.data : undefined,
                    resp?.error,
                    resp?.errors?.[0]?.message,
                ].filter(Boolean);

                const msg = candidates.find((m) => typeof m === 'string' && m.trim().length);
                return (msg || fallback).toString();
            };

            try {
                const { Helper } = await import('@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "helper" */);

                const resp = await Helper.request(ajaxUrl, formdata);

                if (!resp || typeof resp !== 'object') {
                    const text = 'Server returned invalid response';
                    Toast.error(text);
                    showBottomMessage(text, '#dc3545');
                    return;
                }

                if (resp.success) {
                    const okMsg = resp?.message || getMessage(resp, 'Team switched successfully. Reloading...');
                    Toast.success(okMsg);
                    showBottomMessage(okMsg, '#28a745');

                    DOM.el('.my_account_switch_team_modal_wrapper .levcharity_modal_close_button')?.click();
                    setTimeout(() => window.location.reload(), 2000);
                } else {
                    const errMsg = getMessage(resp, 'Something went wrong');
                    Toast.error(errMsg);
                    showBottomMessage(errMsg, '#dc3545');
                }
            } catch (err) {
                console.error(err);

                // Try to pull a message off the thrown error
                let errMsg =
                    (err && typeof err === 'object' && ('message' in err) && err.message) ||
                    'Something went wrong';

                // If Helper.request throws with a structured payload attached (e.g. err.responseJSON)
                if (err?.responseJSON && typeof err.responseJSON === 'object') {
                    errMsg = getMessage(err.responseJSON, errMsg);
                }

                Toast.error(errMsg);
                showBottomMessage(errMsg, '#dc3545');
            }
        });
    }

    async #activateChartBar(bar, timeout, interval) {
        const {gsap} = await import('gsap' /* webpackChunkName: "gsap" */ )
        clearTimeout(timeout)
        const placeholder = DOM.el('.custom_bar_chart_wrapper .placeholder')
        placeholder.textContent = bar.dataset.value

        if (timeout) clearTimeout(timeout);
        if (interval) clearInterval(interval);

        bar.classList.add('active')

        const span = DOM.el('.chart_bar_inner span', bar);

        const animate = () => {
            let top = span.offsetTop - placeholder.offsetHeight - 8;
            let left = bar.offsetLeft;

            if (placeholder.offsetWidth > bar.offsetWidth) {
                left -= (placeholder.offsetWidth - bar.offsetWidth) / 2
            } else {
                left += (bar.offsetWidth - placeholder.offsetWidth) / 2
            }

            gsap.to(placeholder, {
                top: top,
                left: left,
                autoAlpha: 1,
                duration: .2
            })
        }

        interval = setInterval(() => {
            animate();
        }, 50)

        timeout = setTimeout(() => {
            clearInterval(interval);
        }, 100);

        return {interval, timeout}
    }

    async #deactivateChartBar(bar, timeout, interval) {
        const {gsap} = await import('gsap' /* webpackChunkName: "gsap" */ )
        clearInterval(interval)
        bar.classList.remove('active')
        timeout = setTimeout(() => {
            const placeholder = DOM.el('.custom_bar_chart_wrapper .placeholder')
            gsap.to(placeholder, {
                autoAlpha: 0,
                duration: .2,
            })
        }, 300)
        return {interval, timeout}
    }

    #renderChart() {
        const wrapper = DOM.el('.levcharity_my_account_content');
        const statsChartWrapper = DOM.el('.custom_bar_chart_wrapper', wrapper);
        const currencySymbol = Helper.resolveCurrencySymbol(statsChartWrapper.dataset.currencySymbol)
        const progressbar = DOM.el('.progress-bar-wrapper', wrapper);
        const totalsToday = DOM.el('.totals_total', wrapper);
        const raised = DOM.els('.total_raised', wrapper);
        const goal = DOM.el('.goal', wrapper);
        const totalDonors = DOM.el('.total_donors', wrapper);
        const teamMembers = DOM.el('.team_members', wrapper);
        const htmlChart = DOM.el('.html_chart', wrapper);

        let chartMax = 0;
        let fillingBarsCount = 0;
        this.#chartData.data.forEach((bar) => {
            if (bar.value > chartMax) {
                chartMax = bar.value
            }

        })

        const bars = [];

        htmlChart.innerHTML = '';

        if (this.#chartData.data.length) {
            htmlChart.append(...this.#chartData.data.map((bar) => {
                const value = bar.value ? (
                    bar.value / (chartMax / 100)
                ) : 0;

                const barElement = DOM.create('div', {
                    className: ['chart_bar', (!bar.value ? 'empty' : '')],
                    dataValue: currencySymbol + this.#formatAmount(bar.value, 'auto')
                }, [
                    DOM.create('div', {className: 'chart_bar_inner'}, [
                        DOM.create('span')
                    ]),
                    DOM.create('div', {className: 'chart_bar_label', dataLevTooltip: bar.label_description}, bar.label)
                ])

                if (bar.value) {
                    bars.push({
                        barElement,
                        value
                    })
                }

                return barElement;
            }), DOM.create('span', {className: 'placeholder'}));
        } else {
            htmlChart.append(DOM.create('p', {className: 'no-data'}, wp.i18n.__('Start Fundraising to see statistics here', 'w4f')))
        }

        bars.forEach((item, index) => {
            setTimeout(() => {
                DOM.el('.chart_bar_inner span', item.barElement).style.height = item.value + '%'
            }, 50 * (index + 1))
        })

        if (!this.#chartDataAlreadyFetched) {
            totalsToday && this.#numberAnimation(this.#chartData.totalToday, totalsToday, currencySymbol, '', 0, 'auto');
            raised.length && [...raised].forEach(e => this.#numberAnimation(this.#chartData.totalRaised, e, currencySymbol, '', 0, 'auto'));
            goal && this.#numberAnimation(this.#chartData.goal, goal, currencySymbol, '', 0, false);
            totalDonors && this.#numberAnimation(this.#chartData.totalDonors, totalDonors, '', '', 0, false);
            teamMembers && this.#numberAnimation(this.#chartData.teamMembers, teamMembers, '', '', 0, false);
            const percentage = Math.min(100, this.#chartData.totalRaised / (+this.#chartData.goal / 100));
            progressbar.style.setProperty('--progress', percentage)
            if(percentage === 100){
                progressbar && this.#numberAnimation(percentage, DOM.el('.circular-progress-percentage', progressbar), '', '%', null, false)
            }else{
                progressbar && this.#numberAnimation(percentage, DOM.el('.circular-progress-percentage', progressbar), '', '%')
            }
        }
    }

    #renderTotals() {
        const accountsBarWrapper = DOM.el('.accounts_bar');
        if (!accountsBarWrapper) {
            return;
        }
        const currencySymbol = Helper.resolveCurrencySymbol(accountsBarWrapper.dataset.currencySymbol)

        const recentDonations = DOM.el('.recent_donations', accountsBarWrapper);
        const tops = DOM.el('.tops', accountsBarWrapper);
        const topPrizes = DOM.el('.top_prizes', accountsBarWrapper);


        const userSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 124 124" fill="none" class="account-block-avatar" aria-hidden="true">
<circle cx="62" cy="62" r="62" fill="#8B6ECD"/>
<path d="M27.5135 74.2748C27.9639 74.2693 28.7082 74.2653 29.1536 74.2994C30.7853 74.4237 32.5268 76.7198 33.3674 77.9879C34.1208 79.1245 34.7595 80.7291 35.5627 81.8285C37.7358 84.8034 40.3674 87.2389 43.35 89.3909C44.5019 90.2225 46.8962 90.9623 47.8853 92.098C48.4227 92.715 48.1722 93.8801 47.9669 94.5937C47.555 96.0234 46.9231 96.5287 46.6531 98.0906C46.4705 99.1907 46.4272 100.311 46.5242 101.422C46.6473 102.744 46.8928 103.485 46.6948 104.815C45.1005 104.869 44.415 104.734 42.9792 104.004C41.7221 103.204 40.2064 102.81 38.81 101.992C36.354 100.554 33.5464 98.6561 31.4298 96.7441C28.5837 94.056 25.8821 90.8854 23.7045 87.6167C22.8048 86.2663 21.1296 83.0118 21.0218 81.3794C20.8906 79.3906 21.3379 78.0671 22.73 76.6422C24.2468 75.089 25.318 74.354 27.5135 74.2748Z" fill="white"/>
<path d="M95.5701 74.1072C96.7901 74.0304 97.9307 73.9131 99.0976 74.3955C100.203 74.8723 102.166 76.8255 102.51 77.9304C103.201 80.1417 102.98 82.7348 101.689 84.6722C101.106 85.7668 100.546 87.0816 99.8607 88.1049C97.6864 91.349 94.6436 94.8586 91.711 97.4422C89.0442 99.7914 85.2359 102.219 81.9456 103.532C80.2814 104.602 79.4326 104.945 77.3655 104.753C77.1045 102.655 77.8049 100.172 77.3044 97.8588C77.2116 96.3397 76.1804 95.4043 75.9059 93.9398C75.4966 91.947 77.168 91.6896 78.5617 90.8033C82.7293 88.1548 86.1306 84.913 89.2092 81.0526C91.1502 78.6186 91.8205 74.607 95.5701 74.1072Z" fill="white"/>
<path d="M78.25 19.4541C79.5033 19.4043 83.8827 21.4768 85.0694 22.211C91.0845 25.9328 96.8346 31.2238 100.452 37.3529C103.044 41.746 104.843 45.9579 99.764 49.4111C97.777 50.3552 94.7532 50.3989 92.9946 48.9623C91.4636 47.766 90.7727 45.6986 89.6686 44.2136C86.1014 39.4148 82.0139 35.2997 76.7024 32.4566C75.369 31.7431 76.0917 30.4408 76.6398 29.392C77.4885 27.7692 77.7915 25.3236 77.5996 23.4887C77.4314 22.0288 77.2109 21.0295 77.3592 19.6175L78.25 19.4541Z" fill="white"/>
<path d="M45.3881 19.527C45.8453 19.5043 46.2402 19.4205 46.6056 19.6853C46.9879 20.2577 46.5175 22.1572 46.518 23.0376C46.5204 24.294 46.3509 25.5011 46.6742 26.7605C46.9354 27.7782 48.3793 30.5944 48.1134 31.687C47.9915 32.1875 45.3172 33.6635 44.7438 34.0198C41.1597 36.2466 38.3741 39.2306 35.7346 42.4775C33.9771 44.6397 32.8086 48.0369 30.3784 49.5511C29.1381 50.3238 26.0042 50.0559 24.6132 49.6773C23.2311 48.8481 21.4112 46.5972 21.1512 44.9765C20.9034 43.4321 21.255 42.1648 21.9792 40.8146C22.2122 40.3803 22.2505 39.5374 22.5076 39.0765C23.1235 38.015 23.808 36.9378 24.4654 35.9018C24.8516 35.2933 25.5034 34.7101 25.8876 34.1271C27.1809 32.1651 28.615 30.4793 30.3943 28.9351C31.7186 27.7556 33.0671 26.3865 34.5291 25.3706C36.3137 24.1306 38.222 22.8628 40.129 21.8213C40.7029 21.5079 41.4783 21.2042 42.0879 20.9201C43.0726 20.4613 44.3377 19.6984 45.3881 19.527Z" fill="white"/>
<path d="M60.4472 13.1269C66.6297 12.1975 72.3893 16.4687 73.2841 22.6471C74.1796 28.8256 69.8692 34.5516 63.6765 35.4102C57.5336 36.2618 51.8553 32 50.9672 25.8715C50.0791 19.743 54.3146 14.0488 60.4472 13.1269Z" fill="white"/>
<path d="M61.5647 88.9708C67.7907 88.5923 73.1355 93.3476 73.4734 99.5667C73.8113 105.786 69.0139 111.09 62.7831 111.386C56.6109 111.679 51.3586 106.944 51.0238 100.782C50.6889 94.622 55.3972 89.3463 61.5647 88.9708Z" fill="white"/>
</svg>`;
        const renderCard = (title, date, metadata = '', number = true, image = true, amount = 0, cardCurrency = currencySymbol) => {
            const card = DOM.create('div', {className: 'account_block'})
            card.innerHTML = image ? userSVG : '';

            const dateElement = date && DOM.create('span', {className: 'date'}, date);
            const numberElement = number && DOM.create('span', {className: 'number'}, cardCurrency + this.#formatAmount(amount, 'auto'));
            const seperatedPoint = DOM.create('span', {className: 'seperator'}, '•')
            const metadataElement = DOM.create('span', {})
            metadataElement.innerHTML = metadata
            const account_block_texts = DOM.create('div', {className: 'account_block_texts'}, [
                DOM.create('div', {}, title),
                DOM.create('p', {}, [
                    ...(number ? [numberElement] : []),
                    ...(number && date ? [seperatedPoint] : []) ,
                    ...(date ? [dateElement] : [])
                ]),
                metadata ? metadataElement : null
            ])

            card.append(account_block_texts)

            return {
                numberElement,
                card
            }
        }

        recentDonations.innerHTML = '';
        tops.innerHTML = '';
        if (topPrizes) {
            topPrizes.innerHTML = '';
        }

        if(this.#totalsList.recentDonations.length) {
            recentDonations.append(...this.#totalsList.recentDonations.map((donation) => {
                const amount = donation.total ?? donation.conversed_amount ?? 0;
                const currency = Helper.resolveCurrencySymbol(donation.currency, currencySymbol);
                const {
                    numberElement,
                    card
                } = renderCard(donation.first_name + ' ' + donation.last_name, donation.order_date, donation.metadata_html, true, true, amount, currency);

                setTimeout(() => {
                    this.#numberAnimation(amount, numberElement, currency, '', 0, 'auto')
                });

                return card;
            }))
        } else {
            recentDonations.append(DOM.create('p', {className: 'no-data'}, wp.i18n.__('Start Fundraising to see statistics here', 'w4f')))
        }

        if(this.#totalsList.participants.length) {
            tops.append(...this.#totalsList.participants.map((participant) => {
                const {numberElement, card} = renderCard(participant.name, '', participant.created_at);

                setTimeout(() => {
                    this.#numberAnimation(participant.total_amount, numberElement, currencySymbol, '', 0, 'auto')
                });

                return card;
            }))
        } else if(this.#totalsList.topDonations.length) {
            tops.append(...this.#totalsList.topDonations.map((donation) => {
                const amount = donation.total ?? donation.conversed_amount ?? 0;
                const currency = Helper.resolveCurrencySymbol(donation.currency, currencySymbol);
                const {numberElement, card} = renderCard(donation.first_name + ' ' + donation.last_name, donation.order_date, donation.metadata_html, true, true, amount, currency);

                setTimeout(() => {
                    this.#numberAnimation(amount, numberElement, currency, '', 0, 'auto')
                });

                return card;
            }))
        } else {
            tops.append(DOM.create('p', {className: 'no-data'}, wp.i18n.__('Start Fundraising to see statistics here', 'w4f')))
        }

        if (this.#totalsList.topPrizes.length) {
            topPrizes.append(...this.#totalsList.topPrizes.map((prize) => {
                const {numberElement, card} = renderCard(prize.name, '', prize.metadata_html, false, false);

                return card;
            }))
        }
    }

    #formatAmount(value, withFractions = 'auto') {
        const num = Number(value || 0);
        const decimals = this.#amountFractionDigits(num, withFractions);

        return num.toFixed(decimals);
    }

    #amountFractionDigits(num, withFractions) {
        if (withFractions === false) {
            return 0;
        }

        if (withFractions === 'auto') {
            return Math.floor(num) === num ? 0 : 2;
        }

        return 2;
    }

    async #numberAnimation(to, el, prefix, suffix = '', from = 0, withFractions = 'auto') {
        if (!el) {
            return;
        }

        const {gsap} = await import('gsap' /* webpackChunkName: "gsap" */ )
        const tweenObj = { value: Number(from) || 0 };
        const targetValue = Number(to) || 0;

        gsap.to(tweenObj, {
            value: targetValue,
            duration: .8,
            ease: 'power2.inOut',
            onUpdate: () => {
                const value = Number(tweenObj.value) || 0;
                const decimals = this.#amountFractionDigits(value, withFractions);

                el.textContent = prefix + value.toFixed(decimals) + suffix;
            },
        });
    }

        async #datatable() {
        const leaderboardsContainer = DOM.el('.leaderboards-container');
        if (!leaderboardsContainer?.dataset?.id) {
            return;
        }

        const {Helper} = await import('@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "helper" */);

        const _id = leaderboardsContainer.dataset.id;
        let position = -1; // Variable to store the user's position

        Object.keys(window.LevDataTables.datatableInstances).forEach((id) => {
            const DataTable = window.LevDataTables.datatableInstances[id];

            DataTable.on('draw.dt', () => {
                const data = DataTable.context[0].json.data;


                position = data.findIndex(row => _id === row.id);

                const positionString = Helper.ordinalNumberFormatter(position+1)
                const positionElement = DOM.el('.position', leaderboardsContainer);

                if (positionElement) {
                    positionElement.textContent = positionString
                }
            });
        });

        DOM.addEvent('click', '#gotoposition', (event, button) => {
            if (position === -1) {
                return;
            }

            Object.keys(window.LevDataTables.datatableInstances).forEach((id) => {
                const DataTable = window.LevDataTables.datatableInstances[id];

                const pageSize = DataTable.page.len();
                const pageNum = Math.floor(position / pageSize);

                // Paginate to the correct page
                DataTable.page(pageNum).draw('page');

                setTimeout(() => {
                    const rowNode = $(DataTable.row(position).node());

                    if (rowNode.length) {
                        // Scroll into view
                        rowNode[0].scrollIntoView({ behavior: 'smooth', block: 'center' });

                        // Highlight the row
                        rowNode.addClass('highlight');

                        // Remove highlight after 2 seconds
                        setTimeout(() => rowNode.removeClass('highlight'), 2000);
                    }
                }, 300);
            });
        });
    }
}